import { useEffect, useRef, useState } from 'react';

/*
 * Farwings loading screen.
 *   1. ~2,000 particles curve in from the screen edges on fixed, timed paths
 *      (smooth even while the page is busy) and assemble the Farwings bird.
 *   2. FARWINGS rises letter by letter, then the tagline.
 *   3. A 000-100 counter follows the real page load (window load + fonts),
 *      never shorter than ~4.2s so the intro can play.
 *   4. Hand-off: the backdrop melts away and the particles fly into the hero's
 *      particle bird (or the navbar logo on other pages), then dissolve.
 * Shown once per browser session; skipped for prefers-reduced-motion.
 * index.html ships a matching #fw-preload cover so nothing flashes first.
 */

const KEY = 'fw-loaded';
const BG = [4, 10, 21]; // #040A15
const MIN_TIME = 4.2; // seconds the intro always gets
const WORD = 'FARWINGS';
const COLORS = ['#F2F7FC', '#A6A6A6', '#4FB0E7'];

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const lerp = (a, b, t) => a + (b - a) * t;

const reveal = () => {
  document.documentElement.classList.remove('fw-loading');
  document.getElementById('fw-preload')?.remove();
};

function Loader({ onDone }) {
  const R = useRef({});
  const ref = (k) => (el) => {
    R.current[k] = el;
  };

  useEffect(() => {
    const r = R.current;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    window.lenis?.stop();

    const c = r.canvas;
    const ctx = c.getContext('2d');
    let W = 0, H = 0, dpr = 1, raf = 0, cancelled = false;
    let parts = [];
    let buckets = [[], [], []];
    let S = 0, CX = 0, CY = 0; // bird box on the loader

    // readiness
    let loadDone = document.readyState === 'complete';
    let fontsDone = !document.fonts;
    const onLoad = () => { loadDone = true; };
    if (!loadDone) window.addEventListener('load', onLoad);
    document.fonts?.ready.then(() => { fontsDone = true; }, () => { fontsDone = true; });

    const layout = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = window.innerWidth;
      H = window.innerHeight;
      c.width = Math.round(W * dpr);
      c.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      S = Math.round(Math.min(W * 0.62, H * 0.36, 320));
      CX = W / 2;
      CY = H * 0.4;
      r.content.style.top = `${CY + S / 2 + 8}px`;
    };
    layout();

    const img = new Image();
    img.src = '/farwings-mark.png';
    // every particle gets a fixed, timed curve from off-screen into its spot,
    // so the motion stays smooth even if the page is busy loading
    const sample = () => {
      const size = S;
      const gap = size < 240 ? 3 : 4;
      const off = document.createElement('canvas');
      off.width = off.height = size;
      const o = off.getContext('2d', { willReadFrequently: true });
      o.drawImage(img, 0, 0, size, size);
      const d = o.getImageData(0, 0, size, size).data;
      const out = [];
      for (let y = 0; y < size; y += gap) {
        for (let x = 0; x < size; x += gap) {
          const i = (y * size + x) * 4;
          if (d[i + 3] < 150) continue;
          const blue = d[i + 2] > 200 && d[i] < 120;
          const grey = !blue && d[i] < 200;
          const a = Math.random() * Math.PI * 2;
          const far = Math.max(W, H) * (0.6 + Math.random() * 0.5);
          const sx = CX + Math.cos(a) * far;
          const sy = CY + Math.sin(a) * far;
          const tx = CX - S / 2 + (x / size) * S;
          const ty = CY - S / 2 + (y / size) * S;
          // control point bends the path into a gentle swirl
          const mx = (sx + tx) / 2;
          const my = (sy + ty) / 2;
          const bend = (Math.random() * 0.5 + 0.2) * (Math.random() < 0.5 ? -1 : 1);
          out.push({
            nx: x / size,
            ny: y / size,
            sx, sy,
            cx: mx - (ty - sy) * bend,
            cy: my + (tx - sx) * bend,
            x: sx,
            y: sy,
            delay: 0.2 + Math.random() * 1.0,
            dur: 1.7 + Math.random() * 0.7,
            ph: Math.random() * Math.PI * 2,
            c: blue ? 2 : grey ? 1 : 0,
            s: gap * (0.5 + Math.random() * 0.3),
          });
        }
      }
      parts = out;
      buckets = [[], [], []];
      for (const p of parts) buckets[p.c].push(p);
    };

    // destination for the hand-off: the hero bird if it is on screen, else the navbar logo
    const destination = () => {
      const hero = document.querySelector('#home canvas');
      const hb = hero?.getBoundingClientRect();
      if (hb && hb.width > 40 && hb.bottom > 0 && hb.top < H) {
        const size = Math.min(hb.width, hb.height) * 0.92;
        return { x: hb.left + (hb.width - size) / 2, y: hb.top + (hb.height - size) / 2, size };
      }
      const nav = document.querySelector('header a[aria-label^="Farwings"] img');
      const nb = nav?.getBoundingClientRect();
      if (nb && nb.width > 0) {
        const size = Math.min(nb.width, nb.height);
        return { x: nb.left + (nb.width - size) / 2, y: nb.top + (nb.height - size) / 2, size };
      }
      return { x: CX - S / 2, y: CY - S / 2, size: S };
    };

    const t0 = performance.now();
    let last = t0;
    let shown = 0;
    let exitAt = null;
    let dest = null;
    let lastPct = -1;
    const EXIT = 1.6;

    const frame = (now) => {
      if (cancelled) return;
      const t = (now - t0) / 1000;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // counter: smooth time-based curve, held at 90% until the page is really ready
      const ready = (loadDone && fontsDone) || t > 10;
      const goal = Math.min(easeInOut(clamp(t / MIN_TIME)), ready ? 1 : 0.9);
      shown += (goal - shown) * (1 - Math.exp(-dt * 6));
      if (ready && t >= MIN_TIME && shown > 0.996) shown = 1;
      const pct = Math.round(shown * 100);
      if (pct !== lastPct) {
        lastPct = pct;
        r.count.textContent = String(pct).padStart(3, '0');
        r.root.setAttribute('aria-valuenow', String(pct));
      }

      if (shown >= 1 && exitAt === null) exitAt = t + 0.35;
      const e = exitAt === null ? -1 : t - exitAt; // seconds into the hand-off
      if (e >= 0 && !dest) {
        dest = destination();
        for (const p of parts) {
          p.ex = p.x;
          p.ey = p.y;
        }
        reveal(); // hero text starts animating while the loader opens
        r.root.classList.add('is-leaving');
      }

      // soft light trails while assembling; clean frames during the hand-off
      if (e < 0) {
        ctx.fillStyle = `rgba(${BG[0]},${BG[1]},${BG[2]},0.3)`;
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.clearRect(0, 0, W, H);
      }

      const grow = dest ? dest.size / S : 1;
      for (let b = 0; b < 3; b++) {
        ctx.fillStyle = COLORS[b];
        for (const p of buckets[b]) {
          let sz = p.s;
          if (!dest) {
            const u = clamp((t - p.delay) / p.dur);
            if (u <= 0) continue;
            const k = easeOut(u);
            const iu = 1 - k;
            const tx = CX - S / 2 + p.nx * S;
            const ty = CY - S / 2 + p.ny * S;
            p.x = iu * iu * p.sx + 2 * iu * k * p.cx + k * k * tx;
            p.y = iu * iu * p.sy + 2 * iu * k * p.cy + k * k * ty;
            if (u >= 1) {
              // a little life once assembled
              p.x += Math.sin(t * 1.6 + p.ph) * 0.5;
              p.y += Math.cos(t * 1.4 + p.ph) * 0.5;
            }
          } else {
            // sweep across the logo from tail to head
            const v = easeInOut(clamp((e - p.nx * 0.25) / 1.0));
            p.x = lerp(p.ex, dest.x + p.nx * dest.size, v);
            p.y = lerp(p.ey, dest.y + p.ny * dest.size, v);
            sz = p.s * lerp(1, grow, v);
          }
          ctx.fillRect(p.x, p.y, Math.max(1, sz), Math.max(1, sz));
        }
      }

      if (e >= 0) {
        r.bg.style.opacity = String(1 - easeInOut(clamp(e / 0.9)));
        c.style.opacity = String(1 - clamp((e - 1.2) / 0.4));
      }
      if (e >= EXIT) {
        try { sessionStorage.setItem(KEY, '1'); } catch { /* private mode */ }
        onDone();
        return;
      }
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (cancelled) return;
      if (img.naturalWidth) sample();
      r.root.classList.add('is-on');
      raf = requestAnimationFrame(frame);
      requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById('fw-preload')?.remove()));
    };
    if (img.complete && img.naturalWidth) start();
    else {
      img.onload = start;
      img.onerror = start;
    }

    const onResize = () => {
      layout();
      if (img.naturalWidth && !dest) sample();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      img.onload = img.onerror = null;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onLoad);
      html.style.overflow = prevOverflow;
      window.lenis?.start();
      reveal();
    };
  }, [onDone]);

  return (
    <div
      ref={ref('root')}
      role="progressbar"
      aria-label="Loading Farwings"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={0}
      data-liquid-ignore
      className="fw-loader fixed inset-0 z-[300] overflow-hidden"
    >
      {/* backdrop: deep navy, faint grid, slow glow */}
      <div ref={ref('bg')} className="absolute inset-0 bg-[#040A15]">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_60%_55%_at_50%_40%,#000_10%,transparent_75%)]" />
        <div className="fw-loader-glow absolute left-1/2 top-[40%] h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(79,176,231,0.28),transparent)]" />
      </div>

      <canvas ref={ref('canvas')} aria-hidden="true" className="absolute inset-0 h-full w-full" />

      {/* wordmark + tagline under the bird */}
      <div ref={ref('content')} className="fw-loader-text absolute inset-x-0 flex flex-col items-center px-6 text-center" aria-hidden="true">
        <p className="display flex overflow-hidden text-[3.4rem] leading-[1] tracking-[0.04em] text-white sm:text-7xl">
          {WORD.split('').map((ch, i) => (
            <span key={i} className="fw-l inline-block" style={{ '--i': i }}>
              {ch}
            </span>
          ))}
        </p>
        <p className="fw-tag mt-3 text-xs font-semibold uppercase tracking-[0.32em] text-[#9FD3F2] sm:text-sm">
          Your vision &middot; Our technology &middot; Real solutions
        </p>
        <p className="fw-count display tabular mt-7 text-2xl leading-none text-white/80 sm:text-3xl">
          <span ref={ref('count')}>000</span>
          <span className="text-[#4FB0E7]">%</span>
        </p>
      </div>

    </div>
  );
}

export default function SiteLoader() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let skip = false;
    try {
      skip = sessionStorage.getItem(KEY) === '1' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      /* storage blocked: play it */
    }
    if (skip) {
      reveal();
      return;
    }
    setShow(true);
  }, []);

  return show ? <Loader onDone={() => setShow(false)} /> : null;
}
